import { useState, useRef, Fragment } from "react";

import { computeSummary, getItemValue } from "../summaryRules";
import { formatNumber } from "../format";

const HIDDEN_PREFIX = "panflow.hiddenColumns:";
const WIDTHS_PREFIX = "panflow.columnWidths:";

const loadHidden = (key) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || "[]"));
  } catch (error) {
    return new Set();
  }
};

const loadWidths = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (error) {
    return {};
  }
};

const toNum = (value) => {
  const n = Number(String(value).replace(/\s/g, "").replace(",", ".").trim());
  return Number.isNaN(n) ? 0 : n;
};

export default function PaymentTable({
  payments,
  templateColumns,
  actionColumns,
  updatePaymentField,
  deletePayment,
  showActions,
  canDelete,
  templateMode,
  summaryRules = {},
  sections = [],
  matchConditions,
  viewKey = "register"
}) {
  const isTwoLevel = templateMode === "two_level";
  const hiddenKey = HIDDEN_PREFIX + viewKey;
  const widthsKey = WIDTHS_PREFIX + viewKey;
  const tableRef = useRef(null);
  const [expanded, setExpanded] = useState({});
  const [colWidths, setColWidths] = useState(() => loadWidths(widthsKey));
  const [hidden, setHidden] = useState(() => loadHidden(hiddenKey));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [openHeader, setOpenHeader] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [sort, setSort] = useState(null); // { id, dir }
  const [filters, setFilters] = useState({}); // { [colId]: Set<string> }

  // Открывает меню столбца в координатах кнопки (position:fixed — не обрезается таблицей).
  const openMenu = (event, columnId) => {
    if (openHeader === columnId) {
      setOpenHeader(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 310));
    setMenuPos({ top: rect.bottom + 4, left });
    setOpenHeader(columnId);
  };

  const toggle = (id) =>
    setExpanded((current) => ({ ...current, [id]: !current[id] }));

  const toggleColumn = (id) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem(hiddenKey, JSON.stringify([...next]));
      return next;
    });

  const allColumns = [...templateColumns, ...actionColumns];
  const isActionColumn = (column) => actionColumns.some((c) => c.id === column.id);
  const isNumericColumn = (column) => column.key === "amount" || column.format === "number";

  const visibleColumns = templateColumns.filter((column) => !hidden.has(column.id));
  const visibleActionColumns = showActions
    ? actionColumns.filter((column) => !hidden.has(column.id))
    : [];
  const toggleList = [...templateColumns, ...(showActions ? actionColumns : [])];

  // Порядок колонок в таблице (для colgroup и изменения ширины).
  const TOOL = 40;
  const DEFAULT_W = 150;
  const orderedIds = [
    ...(isTwoLevel ? ["__expand__"] : []),
    ...visibleColumns.map((c) => c.id),
    ...visibleActionColumns.map((c) => c.id),
    ...(canDelete ? ["__delete__"] : [])
  ];
  const isToolId = (id) => id === "__expand__" || id === "__delete__";
  const widthOf = (id) => colWidths[id] ?? (isToolId(id) ? TOOL : DEFAULT_W);
  const hasCustomWidths = Object.keys(colWidths).length > 0;
  const tableWidth = hasCustomWidths
    ? orderedIds.reduce((sum, id) => sum + widthOf(id), 0)
    : undefined;

  // Перетаскивание правого края заголовка — как в Excel.
  const startResize = (event, colId) => {
    event.preventDefault();
    event.stopPropagation();

    const ths = tableRef.current
      ? [...tableRef.current.querySelectorAll("thead > tr > th")]
      : [];
    const seeded = { ...colWidths };
    ths.forEach((th, index) => {
      const id = orderedIds[index];
      if (id && seeded[id] == null) {
        seeded[id] = Math.round(th.getBoundingClientRect().width);
      }
    });

    const startX = event.clientX;
    const startWidth = seeded[colId] ?? DEFAULT_W;
    let current = seeded;
    setColWidths(seeded);

    const onMove = (moveEvent) => {
      const width = Math.max(50, startWidth + (moveEvent.clientX - startX));
      current = { ...current, [colId]: width };
      setColWidths(current);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      localStorage.setItem(widthsKey, JSON.stringify(current));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Двойной клик по линии — авто-ширина столбца по содержимому (как в Excel).
  const autoFitColumn = (colId) => {
    const table = tableRef.current;
    if (!table) return;

    const ths = [...table.querySelectorAll("thead > tr > th")];
    const seeded = { ...colWidths };
    ths.forEach((th, index) => {
      const id = orderedIds[index];
      if (id && seeded[id] == null) {
        seeded[id] = Math.round(th.getBoundingClientRect().width);
      }
    });

    let max = 60;
    const safeId = window.CSS && CSS.escape ? CSS.escape(colId) : colId;
    table.querySelectorAll(`[data-col="${safeId}"]`).forEach((cell) => {
      max = Math.max(max, cell.scrollWidth + 20);
    });

    seeded[colId] = Math.min(900, Math.max(50, max));
    setColWidths(seeded);
    localStorage.setItem(widthsKey, JSON.stringify(seeded));
  };

  const isMoneyColumn = (column) => column.key === "amount" || column.format === "number";

  // «Сырое» значение столбца для сортировки/фильтра (без форматирования ₸).
  const cellValue = (payment, column) => {
    if (isActionColumn(column)) return payment.extra_fields?.[column.id] ?? "";
    if (isTwoLevel) {
      const result = computeSummary(column, summaryRules[column.id], payment.items || []);
      return result ? result.value : "";
    }
    return column.key ? (payment[column.key] ?? "") : (payment.extra_fields?.[column.id] ?? "");
  };

  const distinctValues = (column) =>
    [...new Set(payments.map((p) => String(cellValue(p, column))))].sort((a, b) =>
      a.localeCompare(b, "ru", { numeric: true })
    );

  const isAllowed = (colId, value) => (filters[colId] ? filters[colId].has(value) : true);

  const toggleValue = (column, value) =>
    setFilters((prev) => {
      const distinct = distinctValues(column);
      const current = prev[column.id] ? new Set(prev[column.id]) : new Set(distinct);
      if (current.has(value)) current.delete(value);
      else current.add(value);

      const next = { ...prev };
      if (current.size === distinct.length) delete next[column.id];
      else next[column.id] = current;
      return next;
    });

  const toggleAll = (column) =>
    setFilters((prev) => {
      const next = { ...prev };
      if (prev[column.id]) delete next[column.id]; // была частичная/пустая → выбрать все
      else next[column.id] = new Set(); // были все → снять все
      return next;
    });

  const clearFilter = (colId) =>
    setFilters((prev) => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });

  // Применение фильтров и сортировки.
  let rows = payments;
  Object.keys(filters).forEach((colId) => {
    const column = allColumns.find((c) => c.id === colId);
    const allowed = filters[colId];
    if (column && allowed) {
      rows = rows.filter((p) => allowed.has(String(cellValue(p, column))));
    }
  });
  if (sort) {
    const column = allColumns.find((c) => c.id === sort.id);
    if (column) {
      const numeric = isNumericColumn(column);
      rows = [...rows].sort((a, b) => {
        const va = cellValue(a, column);
        const vb = cellValue(b, column);
        const cmp = numeric
          ? toNum(va) - toNum(vb)
          : String(va).localeCompare(String(vb), "ru", { numeric: true });
        return sort.dir === "desc" ? -cmp : cmp;
      });
    }
  }
  const displayedPayments = rows;

  const summaryDisplay = (payment, column) => {
    const value = cellValue(payment, column);
    if (!isTwoLevel) {
      if (isMoneyColumn(column)) {
        if (String(value).trim() === "") return "";
        return column.key === "amount" ? `${formatNumber(value)} ₸` : formatNumber(value);
      }
      return value;
    }
    const rule = summaryRules[column.id];
    const result = computeSummary(column, rule, payment.items || []);
    if (!result) return "";
    if (result.isNumber) {
      const money = isMoneyColumn(column) && rule !== "count";
      return money ? `${formatNumber(result.value)} ₸` : formatNumber(result.value);
    }
    return result.value;
  };

  const renderActionCells = (p) =>
    visibleActionColumns.map((column) => (
      <td key={column.id}>
        <select
          className="action-select"
          value={p.extra_fields?.[column.id] || ""}
          onChange={(e) => updatePaymentField(p.id, column.id, e.target.value)}
          aria-label={`${column.label} для ${p.payment_number}`}
        >
          <option value=""></option>
          {column.options.map((option) => (
            <option key={option.id} value={option.value}>{option.label}</option>
          ))}
        </select>
      </td>
    ));

  const renderDeleteCell = (p) =>
    canDelete && (
      <td className="delete-cell">
        <button
          className="delete-btn"
          aria-label="Удалить платеж"
          title="Удалить платеж"
          onClick={() => deletePayment(p.id)}
        >
          &#128465;
        </button>
      </td>
    );

  const renderDetail = (p) => {
    const items = p.items || [];
    if (items.length === 0) {
      return (
        <tr className="detail-item">
          {isTwoLevel && <td className="expand-cell"></td>}
          <td colSpan={visibleColumns.length || 1}>Нет строк.</td>
          {visibleActionColumns.map((column) => <td key={column.id}></td>)}
          {canDelete && <td></td>}
        </tr>
      );
    }
    return items.map((item, index) => (
      <tr className="detail-item" key={`${p.id}-item-${index}`}>
        {isTwoLevel && <td className="expand-cell"></td>}
        {visibleColumns.map((column) => {
          const value = getItemValue(item, column);
          const formatted =
            isMoneyColumn(column) && String(value).trim() !== ""
              ? (column.key === "amount" ? `${formatNumber(value)} ₸` : formatNumber(value))
              : value;
          return <td key={column.id} data-col={column.id} title={String(formatted ?? "")}>{formatted}</td>;
        })}
        {visibleActionColumns.map((column) => <td key={column.id}></td>)}
        {canDelete && <td></td>}
      </tr>
    ));
  };

  const renderPaymentRow = (p) => (
    <Fragment key={p.id}>
      <tr>
        {isTwoLevel && (
          <td className="expand-cell">
            <button className="expand-btn" aria-label="Показать строки" onClick={() => toggle(p.id)}>
              {expanded[p.id] ? "▾" : "▸"}
            </button>
          </td>
        )}
        {visibleColumns.map((column) => {
          const text = summaryDisplay(p, column);
          return <td key={column.id} data-col={column.id} title={String(text ?? "")}>{text}</td>;
        })}
        {renderActionCells(p)}
        {renderDeleteCell(p)}
      </tr>
      {isTwoLevel && expanded[p.id] && renderDetail(p)}
    </Fragment>
  );

  const renderHeader = (column) => {
    const active = Boolean(filters[column.id]) || sort?.id === column.id;
    const values = distinctValues(column);

    return (
      <th key={column.id} title={column.label} data-col={column.id}>
        <div className="th-inner">
          <span>{column.label}</span>
          <button
            className={`th-menu-btn ${active ? "active" : ""}`}
            aria-label={`Сортировка и фильтр: ${column.label}`}
            onClick={(event) => openMenu(event, column.id)}
          >
            ▾
          </button>
        </div>

        {openHeader === column.id && (
          <>
            <div className="th-menu-overlay" onClick={() => setOpenHeader(null)} />
            <div className="th-menu" style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}>
              <button className="th-menu-item" onClick={() => { setSort({ id: column.id, dir: "asc" }); setOpenHeader(null); }}>
                Сортировка от А до Я
              </button>
              <button className="th-menu-item" onClick={() => { setSort({ id: column.id, dir: "desc" }); setOpenHeader(null); }}>
                Сортировка от Я до А
              </button>

              <div className="th-menu-sep" />

              <button className="th-menu-item" onClick={() => clearFilter(column.id)}>
                Очистить фильтр
              </button>

              <label className="th-menu-check">
                <input type="checkbox" checked={!filters[column.id]} onChange={() => toggleAll(column)} />
                (Выделить все)
              </label>

              <div className="th-menu-values">
                {values.map((value) => (
                  <label className="th-menu-check" key={value}>
                    <input
                      type="checkbox"
                      checked={isAllowed(column.id, value)}
                      onChange={() => toggleValue(column, value)}
                    />
                    {value === "" ? "(пусто)" : value}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <span
          className="col-resize-handle"
          title="Потяните — ширина. Двойной клик — по содержимому."
          onMouseDown={(event) => startResize(event, column.id)}
          onDoubleClick={() => autoFitColumn(column.id)}
        />
      </th>
    );
  };

  const colSpanCount =
    (isTwoLevel ? 1 : 0) + visibleColumns.length + visibleActionColumns.length + (canDelete ? 1 : 0);

  const renderBand = (label, key) => (
    <tr className="section-band" key={key}>
      <td colSpan={colSpanCount}>{label}</td>
    </tr>
  );

  // Разделы внутри вкладки: полоса-заголовок + строки, подходящие под её условия.
  const renderBody = () => {
    if (!sections.length || !matchConditions) {
      return displayedPayments.map(renderPaymentRow);
    }

    const out = [];
    const used = new Set();

    sections.forEach((section) => {
      const rows = displayedPayments.filter(
        (p) => !used.has(p.id) && matchConditions(p, section.conditions)
      );
      rows.forEach((p) => used.add(p.id));
      out.push(renderBand(section.label || "Раздел", `band-${section.id}`));
      rows.forEach((p) => out.push(renderPaymentRow(p)));
    });

    const rest = displayedPayments.filter((p) => !used.has(p.id));
    if (rest.length) {
      out.push(renderBand("Без раздела", "band-none"));
      rest.forEach((p) => out.push(renderPaymentRow(p)));
    }

    return out;
  };

  return (
    <div className="table-wrapper">
      <div className="columns-toolbar">
        <button className="columns-btn" onClick={() => setColumnsOpen((open) => !open)}>
          Столбцы ▾
        </button>

        {columnsOpen && (
          <div className="columns-menu">
            {toggleList.map((column) => (
              <label key={column.id}>
                <input type="checkbox" checked={!hidden.has(column.id)} onChange={() => toggleColumn(column.id)} />
                {column.label}
              </label>
            ))}
          </div>
        )}
      </div>

      <table className="table" ref={tableRef} style={tableWidth ? { width: `${tableWidth}px` } : undefined}>
        {hasCustomWidths && (
          <colgroup>
            {orderedIds.map((id) => (
              <col key={id} style={{ width: `${widthOf(id)}px` }} />
            ))}
          </colgroup>
        )}

        <thead>
          <tr>
            {isTwoLevel && <th className="tools-col"></th>}
            {visibleColumns.map(renderHeader)}
            {visibleActionColumns.map(renderHeader)}
            {canDelete && <th className="tools-col"></th>}
          </tr>
        </thead>

        <tbody>
          {renderBody()}
        </tbody>
      </table>
    </div>
  );
}
